import type { ReactNode } from "react";
import { formatPrice, formatR, istDateTime } from "../../lib/format.js";
import type { SignalView } from "../../lib/queries.js";
import { ReturnCell, StatusCell } from "../signals/SignalCells.js";

/**
 * The plan and how it is doing, off the same quote the feed row used.
 *
 * The modal is where a reader lands after a row catches their eye, so the
 * numbers that caught it have to survive the click. Without this the detail
 * view dropped entry, stop, target, and the live price entirely, and the
 * quote the route already fetches paid for nothing.
 *
 * `ReturnCell` and `StatusCell` are the feed's own cells rather than copies:
 * a return signed to the side and a status read off one price are exactly the
 * things that must not say two different things on two screens.
 */
export function SignalPlan({ signal }: { signal: SignalView }) {
  const { trade, quote } = signal;
  // A rank crossing names no entry and may not be quoted at all. Nothing to
  // show is better than a row of dashes pretending there was a plan.
  if (trade === null && quote === null) return null;

  return (
    <section
      className="flex flex-col gap-2 border border-border bg-surface-raised px-4 py-3"
      data-testid="signal-plan"
    >
      <dl className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
        {trade ? (
          <>
            <Fact label="Entry">{formatPrice(trade.plan.entry)}</Fact>
            <Fact label="Stop">{formatPrice(trade.plan.stop)}</Fact>
            <Fact label="Target">{formatPrice(trade.plan.target)}</Fact>
          </>
        ) : null}
        <Fact label="Last">{formatPrice(quote?.lastPrice)}</Fact>
        {trade?.progress ? (
          <>
            <Fact label="Since">
              <ReturnCell trade={trade} />
            </Fact>
            <Fact label="R">{formatR(trade.progress.rMultiple)}</Fact>
            <Fact label="Status">
              <StatusCell trade={trade} />
            </Fact>
          </>
        ) : null}
      </dl>

      {/* The same caveat the symbol page carries, for the same reason: every
          number to the right of Last is read off this one snapshot, and an
          unmarked Friday quote read on Sunday looks live. */}
      <p className="text-xs text-text-muted" data-testid="signal-plan-provenance">
        {quote
          ? `Quoted ${istDateTime(quote.fetchedAt)}`
          : "No live quote, so there is nothing to measure the plan against yet."}
      </p>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-xs uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="tabular-nums text-text">{children}</dd>
    </div>
  );
}
